import { createFileRoute, notFound, redirect, useRouter } from '@tanstack/react-router'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import {
  DropifyApiError,
  getProductPrimaryImage,
  getStorefrontConfig,
  getStorefrontProduct,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontProduct } from '../lib/dropify'
import { DEFAULT_OG_IMAGE, absoluteUrl, brandTitle, buildBreadcrumbSchema, buildFaqSchema, buildProductFaq, productMetaDescription } from '../lib/seo'
import { getProductOrderFacts, isGekkoProduct, isMaskProduct, isMedicalGloveProduct, isNonMedicalMaskProduct, normalizeProductOrderingFacts } from '../lib/productOrdering'
import { StorefrontUnavailable } from '../components/StorefrontUnavailable'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'
import { getProductLandingPilot } from '../data/productLandingPilots'
import { getProductLandingContent, ensureMinimumProductFaq } from '../data/productLandingContent'
import { ProductLandingPage } from '../components/ProductLandingPage'

const WA_NUMBER = '6285704011028'
const PRODUCT_OG_IMAGE_TRANSFORM = { width: 1200, height: 1200, fitIn: true, format: 'jpeg', quality: 82 } as const

const LEGACY_PRODUCT_REDIRECTS: Record<string, string> = {
  'kaos-kaki-bola-football-bo265': '/produk/kaos-kaki-bola-vieri',
  'masker-onemed-kn95-4-ply-putih-box-25-pcs': '/produk/masker-onemed-kn95-4-ply-putih-box-20-pcs',
}

export function getLegacyProductRedirect(slug: string) {
  return LEGACY_PRODUCT_REDIRECTS[slug] ?? null
}

export function waOrderLink(product: StorefrontProduct) {
  const message = isMaskProduct(product)
    ? `Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ box, tipe atau warna: ___, kirim ke ___. Mohon info stok, isi per box, quotation, dan dokumen produk yang tersedia.`
    : isMedicalGloveProduct(product)
      ? `Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ box, ukuran: ___, jumlah per ukuran: ___, kirim ke ___. Mohon info stok, quotation, dan dokumen produk yang tersedia.`
      : isNonMedicalMaskProduct(product)
        ? `Halo Dropify, saya mau cek ${product.name}. Kebutuhan: ___ lusin, pemakaian: ___, kirim ke ___. Mohon info stok dan quotation.`
    : isGekkoProduct(product)
      ? `Halo Dropify, saya mau cek ${product.name}. Pemakaian untuk: ___. Kebutuhan: ___ lusin. Kota tujuan: ___. Bisa kirim foto stok aktual dan quotation?`
      : `Halo Dropify, saya tertarik dengan ${product.name}. Kebutuhan sekitar ___ lusin, kirim ke ___. Mohon informasi stok, satuan pembelian, dan quotation.`

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    message,
  )}`
}

export function productFaqFor(product: StorefrontProduct) {
  const orderFacts = getProductOrderFacts(product)

  if (isMaskProduct(product)) {
    return ensureMinimumProductFaq(product, [
      {
        q: `Berapa minimum order ${product.name}?`,
        a: `${orderFacts?.minimumLabel ?? 'Minimum pemesanan dikonfirmasi admin sebelum quotation.'}${orderFacts?.packLabel ? ` ${orderFacts.packLabel}` : ''}`,
      },
      {
        q: `Tipe apa yang perlu dipilih untuk ${product.name}?`,
        a: `Konfirmasikan tipe masker, warna, jumlah box, dan kebutuhan penggunaan. Isi per box mengikuti model yang tercantum pada nama produk dan perlu diperiksa kembali saat pemesanan.`,
      },
      {
        q: `Apakah dokumen produk ${product.name} dapat diminta sebelum PO?`,
        a: `Bisa. Kirim daftar dokumen yang diwajibkan. Admin akan mencocokkan dokumen dan masa berlakunya dengan SKU masker yang dipilih.`,
      },
      {
        q: `Bagaimana cara memesan ${product.name}?`,
        a: `Kirim tipe, warna bila tersedia, jumlah box, kota tujuan, target penerimaan, dan kebutuhan dokumen melalui WhatsApp. Admin akan memeriksa stok, isi per box, harga, ongkir, dan menyiapkan quotation.`,
      },
    ])
  }

  if (isMedicalGloveProduct(product)) {
    return ensureMinimumProductFaq(product, [
      {
        q: `Berapa minimum order ${product.name}?`,
        a: `${orderFacts?.minimumLabel ?? 'Minimum pemesanan mulai 10 box.'} Kirim ukuran dan pembagian jumlah per ukuran agar admin dapat memeriksa stok sebelum menyiapkan quotation.`,
      },
      {
        q: `Ukuran apa saja yang tersedia untuk ${product.name}?`,
        a: `${orderFacts?.sizeGuidance ?? 'Ukuran tersedia mulai XS sampai XL. Admin akan mengonfirmasi ukuran yang tersedia untuk produk yang Anda pilih.'}`,
      },
      {
        q: `Apakah dokumen produk ${product.name} dapat diminta sebelum PO?`,
        a: `Bisa. Kirim daftar dokumen yang diwajibkan, misalnya izin edar AKD Kemenkes, TKDN, halal, food grade, CE atau MDR. Admin akan mencocokkan dokumen dan masa berlakunya dengan SKU yang dipilih.`,
      },
      {
        q: `Bagaimana cara memesan ${product.name}?`,
        a: `Kirim ukuran, jumlah per ukuran, total box, kota tujuan, target penerimaan, dan kebutuhan dokumen melalui WhatsApp. Admin akan memeriksa stok, harga, ongkir, dan menyiapkan quotation.`,
      },
    ])
  }

  if (isNonMedicalMaskProduct(product)) {
    return ensureMinimumProductFaq(product, [
      {
        q: `Berapa minimum order ${product.name}?`,
        a: `${orderFacts?.minimumLabel ?? 'Minimum order dikonfirmasi admin sebelum quotation.'} ${orderFacts?.packLabel ?? ''}`.trim(),
      },
      {
        q: `Bagaimana cara memesan ${product.name}?`,
        a: `Kirim jumlah lusin, kebutuhan pemakaian, kota tujuan, dan target penerimaan melalui WhatsApp. Admin akan memeriksa stok dan menyiapkan quotation.`,
      },
    ])
  }

  if (!isGekkoProduct(product)) return ensureMinimumProductFaq(product, buildProductFaq(product))

  return ensureMinimumProductFaq(product, [
    {
      q: `Berapa minimum order ${product.name}?`,
      a: `${orderFacts?.minimumLabel ?? 'Minimum order Gekko Grip mulai 50 lusin atau 600 pasang.'} ${orderFacts?.packLabel ?? 'Satu lusin berisi 12 pasang.'} Harga ${product.name} mengikuti volume dan stok saat penawaran dibuat.`,
    },
    {
      q: `Apakah bisa meminta sample ${product.name}?`,
      a: `Bisa. Sample membantu tim Anda memeriksa ukuran, kenyamanan, grip, dan kecocokan di area kerja. Biaya sample dihitung dengan harga satuan ditambah ongkir.`,
    },
    {
      q: `Apakah marking dan dokumen CE atau EN388 tersedia?`,
      a: `Marking dan dokumen keselamatan harus diperiksa per seri. Kirim standar yang diwajibkan oleh tim K3 atau dokumen tender agar admin dapat mengonfirmasi barang yang tersedia sebelum PO.`,
    },
    {
      q: `Bagaimana cara memesan ${product.name}?`,
      a: `Kirim jenis pekerjaan, kondisi permukaan benda, jumlah kebutuhan, ukuran bila ada, standar wajib, dan kota tujuan melalui WhatsApp. Admin akan mengirim foto stok aktual, harga volume, dokumen yang tersedia, dan ongkir.`,
    },
  ])
}

export const Route = createFileRoute('/produk/$id')({
  loader: async ({ params }) => {
    const legacyPath = getLegacyProductRedirect(params.id)
    if (legacyPath) throw redirect({ href: legacyPath, statusCode: 301 })

    const [config, product] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      getStorefrontProduct(params.id).catch((error) => {
        // A deleted/unknown product should serve a 404, not a 500.
        if (error instanceof DropifyApiError && error.status === 404) throw notFound()
        return null
      }),
    ])

    if (!product) return { config, product: null, related: [], nextProduct: null, unavailable: true }
    const normalizedProduct = normalizeProductOrderingFacts(product)
    const pilot = getProductLandingPilot(normalizedProduct)
    const primaryCategorySlug = normalizedProduct.categories[0]?.slug
    const relatedResponse = await (pilot ? Promise.resolve({ items: [] as StorefrontProduct[] }) : listStorefrontProducts({
      category: primaryCategorySlug,
      per_page: 100,
      sort_by: 'published_at',
      sort_dir: 'desc',
    }).catch(() => ({ items: [] as StorefrontProduct[] })))

    const items = relatedResponse.items.map(normalizeProductOrderingFacts)
    const currentIdx = items.findIndex((item) => item.id === normalizedProduct.id)
    const sameProductFamily = items.filter((item) =>
      isMaskProduct(normalizedProduct) ? isMaskProduct(item) : !isMaskProduct(item),
    )
    const others = sameProductFamily.filter((item) => item.id !== normalizedProduct.id)

    let nextProduct: StorefrontProduct | null = null
    if (others.length > 0) {
      if (currentIdx >= 0) {
        const candidate = sameProductFamily[(sameProductFamily.findIndex((item) => item.id === normalizedProduct.id) + 1) % sameProductFamily.length]
        nextProduct = candidate.id === normalizedProduct.id ? null : candidate
      } else {
        nextProduct = others[0]
      }
    }

    return {
      config,
      product: normalizedProduct,
      related: others.slice(0, 4),
      nextProduct,
      unavailable: false,
    }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.unavailable ?? false),
  head: ({ loaderData, params }) => {
    const product = loaderData?.product
    const pilot = product ? getProductLandingPilot(product) : null
    const landingContent = product ? getProductLandingContent(product, loaderData?.related ?? [], productFaqFor(product)) : null
    const title = product && pilot ? brandTitle(`Grosir ${product.name}`) : product?.meta_title ?? (product ? brandTitle(`Grosir ${product.name}`) : 'Detail Produk Grosir | Dropify')
    const description = product ? productMetaDescription(product) : 'Detail produk grosir dari Dropify, distributor tangan pertama di Indonesia.'
    const productPath = `/produk/${encodeURIComponent(product?.slug || product?.id || params.id)}`
    const image = product ? absoluteUrl(getProductPrimaryImage(product, PRODUCT_OG_IMAGE_TRANSFORM)) : DEFAULT_OG_IMAGE

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'product' },
        { property: 'og:url', content: absoluteUrl(productPath) },
        { property: 'og:image', content: image },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl(productPath) },
      ],
      scripts: product ? [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            ...(landingContent ? [{ name: landingContent.parentLabel, path: landingContent.parentHref }] : []),
            { name: product.name, path: productPath },
          ])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(landingContent?.faq ?? [])),
        },
      ] : [],
    }
  },
  component: ProductDetailPage,
})

function ProductDetailPage() {
  const data = Route.useLoaderData()
  const { id } = Route.useParams()
  const router = useRouter()
  if (!data.product) return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={data.config} /><HeaderMiddle /><Navbar />
      <main className="px-4 py-16"><h1 className="mb-6 text-center text-2xl font-bold">Detail produk belum tersedia</h1>
        <StorefrontUnavailable path={`/produk/${id}`} onRetry={() => router.invalidate()} />
      </main><Footer config={data.config} />
    </div>
  )
  const content = getProductLandingContent(data.product, data.related, productFaqFor(data.product))
  return <ProductLandingPage config={data.config} product={data.product} content={content} />
}
